import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CountTabs } from '@/shared/components/CountTabs';
import { EmptyState } from '@/shared/components/EmptyState';
import { ErrorState } from '@/shared/components/ErrorState';
import { ListPagination } from '@/shared/components/ListPagination';
import { PageHeader } from '@/shared/components/PageHeader';
import { useListUrlState } from '@/shared/hooks/useListUrlState';
import { useRoleStore } from '@/shared/session/roleStore';
import type { DeskQuestion } from '../api/deskApi';
import { DeskDetail } from '../components/DeskDetail';
import { DeskList } from '../components/DeskList';
import { useDeskCount, useDeskQuestionsQuery } from '../hooks/useDesk';

const DEFAULTS = { pageSize: 20, filters: { status: 'open' } };

const EMPTY = {
  open: { title: 'No questions waiting', hint: 'RMs send questions here from a "not found" answer.' },
  answered: { title: 'No answered questions yet', hint: 'Replies to RMs are kept here.' },
};

function Loading() {
  return (
    <Box sx={{ ...frameSx, minHeight: 420 }} aria-busy="true" aria-label="Loading questions">
      <Stack sx={{ p: 2, gap: 2 }}>{[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={72} />)}</Stack>
      <Box sx={{ p: 3, display: { xs: 'none', md: 'block' } }}><Skeleton variant="rounded" height={160} /></Box>
    </Box>
  );
}

type SplitProps = { questions: DeskQuestion[]; total: number; empty: { title: string; hint: string }; selectedId: string; onSelect: (id: string) => void; onAnswered: () => void; footer: ReactNode };

/** The list on the left and the selected question on the right; stacked on a phone. */
function Split({ questions, total, empty, selectedId, onSelect, onAnswered, footer }: SplitProps) {
  const role = useRoleStore((s) => s.role);
  if (total === 0) return <EmptyState {...empty} />;
  const selected = questions.find((q) => q.id === selectedId) ?? questions[0];
  return (
    <Box sx={frameSx}>
      <Box sx={{ borderStyle: 'solid', borderColor: 'divider', borderWidth: 0, borderRightWidth: { md: 1 }, borderBottomWidth: { xs: 1, md: 0 }, minWidth: 0 }}>
        <DeskList questions={questions} selected={selected?.id ?? ''} onSelect={onSelect} />
        {footer}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {selected ? <DeskDetail q={selected} canAnswer={role === 'analyst'} role={role} onAnswered={onAnswered} /> : null}
      </Box>
    </Box>
  );
}

/** Questions the assistant could not answer: the waiting ones for analysts to reply to, and the replies sent. */
export function DeskPage() {
  const list = useListUrlState(DEFAULTS);
  const [searchParams, setSearchParams] = useSearchParams();
  const status = list.filters.status === 'answered' ? 'answered' : 'open';
  const query = useDeskQuestionsQuery(list.params);
  const waiting = useDeskCount('open').data;
  const answered = useDeskCount('answered').data;
  const select = (id: string) => {
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('q', id); return next; }, { replace: true });
  };
  const { data } = query;

  return (
    <>
      <PageHeader
        title="Desk questions"
        description="Questions the assistant couldn't answer. Your reply goes back to the RM where they asked, and is kept here."
        actions={
          <CountTabs
            label="Question status"
            value={status}
            onChange={(v) => { list.setFilters({ status: v }); }}
            options={[{ value: 'open', label: 'Waiting', count: waiting }, { value: 'answered', label: 'Answered', count: answered }]}
          />
        }
      />
      {query.isError ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : null}
      {!data && !query.isError ? <Loading /> : null}
      {data ? (
        <Split
          empty={EMPTY[status]}
          questions={data.items}
          total={data.total}
          selectedId={searchParams.get('q') ?? ''}
          onSelect={select}
          // The reply moves the question to Answered; follow it there, still selected.
          onAnswered={() => { list.setFilters({ status: 'answered' }); }}
          footer={data.total > list.pageSize ? (
            <ListPagination total={data.total} page={list.page} pageSize={list.pageSize} onPageChange={list.setPage} onPageSizeChange={list.setPageSize} />
          ) : null}
        />
      ) : null}
    </>
  );
}

const frameSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 340px) minmax(0, 1fr)' },
  minHeight: { md: 520 },
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1.5,
  overflow: 'hidden',
} as const;
